import React, { useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  Badge,
  Spinner,
  Table,
  Button,
  ButtonGroup,
} from 'reactstrap';
import Header from 'components/Headers/Header';
import Swal from 'sweetalert2';
import { fetchMyGroupOrders } from 'redux/GroupOrders/GroupOrdersActions';
import { getPlanById } from 'config/subscriptionPlans';

const GroupCodes = (props) => {
  const dispatch = useDispatch();
  const [sortConfig, setSortConfig] = useState({});
  const [filterConfig, setFilterConfig] = useState({});

  useEffect(() => {
    dispatch(fetchMyGroupOrders());
  }, []);

  const { data: rawOrders, loading } = props.fetchMyGroupOrders;

  // Filter orders by active specialty at the order level.
  // Orders without a specialty field are treated as 'pmr' (backwards compatible).
  const activeSpecialty = props.activeSpecialty || 'pmr';
  const orders = (rawOrders || []).filter(
    (order) => (order.specialty || 'pmr') === activeSpecialty
  );

  const copyCode = (code) => {
    navigator.clipboard.writeText(code).then(() => {
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: 'Copied!',
        showConfirmButton: false,
        timer: 800,
      });
    });
  };

  const copyAllCodes = (codes) => {
    if (!codes || codes.length === 0) return;
    const text = codes.map((c) => c.code).join('\n');
    navigator.clipboard.writeText(text).then(() => {
      Swal.fire({
        title: 'Copied!',
        text: `${codes.length} codes copied to clipboard.`,
        icon: 'success',
        timer: 1500,
        showConfirmButton: false,
      });
    });
  };

  const copyAllUnclaimed = (codes) => {
    const unclaimed = codes.filter((c) => c.status === 'unclaimed').map((c) => c.code);
    if (unclaimed.length === 0) {
      Swal.fire('No unclaimed codes', 'All codes have been redeemed.', 'info');
      return;
    }
    navigator.clipboard.writeText(unclaimed.join('\n')).then(() => {
      Swal.fire({
        title: 'Copied!',
        text: `${unclaimed.length} unclaimed codes copied.`,
        icon: 'success',
        timer: 1500,
        showConfirmButton: false,
      });
    });
  };

  const handleSort = (orderId, key) => {
    setSortConfig((prev) => {
      const current = prev[orderId];
      if (current?.key === key) {
        return { ...prev, [orderId]: { key, direction: current.direction === 'asc' ? 'desc' : 'asc' } };
      }
      return { ...prev, [orderId]: { key, direction: 'asc' } };
    });
  };

  const getFilteredCodes = (codes, orderId) => {
    const filter = filterConfig[orderId] || 'all';
    if (filter === 'all') return codes;
    if (filter === 'available') return codes.filter((c) => c.status === 'unclaimed');
    if (filter === 'redeemed') return codes.filter((c) => c.status === 'claimed');
    return codes;
  };

  const getSortedCodes = (codes, orderId) => {
    const config = sortConfig[orderId];
    if (!config) return codes;

    return [...codes].sort((a, b) => {
      let aVal, bVal;
      if (config.key === 'status') {
        aVal = a.status;
        bVal = b.status;
      } else if (config.key === 'redeemedBy') {
        aVal = (a.claimedByHandle || '').toLowerCase();
        bVal = (b.claimedByHandle || '').toLowerCase();
      } else if (config.key === 'claimedAt') {
        aVal = a.claimedAt || '';
        bVal = b.claimedAt || '';
      }
      if (aVal < bVal) return config.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return config.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const getProcessedCodes = (codes, orderId) => {
    return getSortedCodes(getFilteredCodes(codes, orderId), orderId);
  };

  const SortIcon = ({ orderId, columnKey }) => {
    const config = sortConfig[orderId];
    const isActive = config?.key === columnKey;
    return (
      <i
        className={`fas fa-sort${isActive ? (config.direction === 'asc' ? '-up' : '-down') : ''} ml-1`}
        style={{ opacity: isActive ? 1 : 0.3, fontSize: '0.75rem' }}
      />
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <Row>
          <Col>
            <Card className="shadow">
              <CardHeader>
                <h3 className="mb-0">My Group Codes</h3>
                <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                  Manage access codes for your residency program
                </p>
              </CardHeader>
              <CardBody>
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner />
                  </div>
                ) : orders.length === 0 ? (
                  <div className="text-center py-5">
                    <i
                      className="fas fa-ticket-alt text-muted"
                      style={{ fontSize: '3rem' }}
                    />
                    <h4 className="mt-3 text-muted">No Group Orders Found</h4>
                    <p className="text-muted">
                      Group orders associated with your email will appear here.
                    </p>
                  </div>
                ) : (
                  orders.map((order) => {
                    const isIndividual = order.paymentType === 'individual';
                    const claimedCount = order.codes ? order.codes.filter((c) => c.status === 'claimed').length : 0;
                    const totalCount = order.codes ? order.codes.length : 0;
                    const paidCount = isIndividual && order.codes ? order.codes.filter((c) => c.paymentStatus === 'paid').length : 0;
                    const activatedCount = isIndividual && order.codes ? order.codes.filter((c) => c.subscriptionActivated).length : 0;
                    const activeFilter = filterConfig[order.id] || 'all';

                    return (
                      <Card key={order.id} className="mb-4 border">
                        <CardHeader>
                          <Row className="align-items-center">
                            <Col>
                              <h5 className="mb-0">{order.programName}{order.year ? ` — ${order.year}` : ''}</h5>
                              <small className="text-muted">
                                {order.items
                                  ? order.items.map((it) => `${it.quantity}x ${getPlanById(it.planId)?.name || it.planId}`).join(', ')
                                  : (getPlanById(order.planId)?.name || order.planId)}
                                {isIndividual
                                  ? ` | ${paidCount}/${totalCount} paid | ${activatedCount} activated`
                                  : ` | Codes — ${claimedCount} claimed / ${totalCount} total`}
                                {' | '}
                                <Badge
                                  color={order.status === 'paid' || order.status === 'active' ? 'success' : 'warning'}
                                >
                                  {order.status}
                                </Badge>
                                {isIndividual && (
                                  <Badge color="info" className="ml-1">Individual</Badge>
                                )}
                              </small>
                            </Col>
                            <Col className="text-right">
                              {order.codes && order.codes.length > 0 && (
                                <>
                                  <Button
                                    size="sm"
                                    color="default"
                                    outline
                                    className="mr-1"
                                    onClick={() => copyAllCodes(order.codes)}
                                  >
                                    <i className="fas fa-copy mr-1" /> Copy All
                                  </Button>
                                  <Button
                                    size="sm"
                                    color="primary"
                                    outline
                                    onClick={() => copyAllUnclaimed(order.codes)}
                                  >
                                    <i className="fas fa-copy mr-1" /> Copy Unclaimed
                                  </Button>
                                </>
                              )}
                            </Col>
                          </Row>
                          {order.codes && order.codes.length > 0 && (
                            <Row className="mt-3">
                              <Col>
                                <ButtonGroup size="sm">
                                  <Button
                                    color={activeFilter === 'all' ? 'primary' : 'secondary'}
                                    outline={activeFilter !== 'all'}
                                    onClick={() => setFilterConfig((prev) => ({ ...prev, [order.id]: 'all' }))}
                                  >
                                    All ({totalCount})
                                  </Button>
                                  <Button
                                    color={activeFilter === 'available' ? 'warning' : 'secondary'}
                                    outline={activeFilter !== 'available'}
                                    onClick={() => setFilterConfig((prev) => ({ ...prev, [order.id]: 'available' }))}
                                  >
                                    Available ({totalCount - claimedCount})
                                  </Button>
                                  <Button
                                    color={activeFilter === 'redeemed' ? 'success' : 'secondary'}
                                    outline={activeFilter !== 'redeemed'}
                                    onClick={() => setFilterConfig((prev) => ({ ...prev, [order.id]: 'redeemed' }))}
                                  >
                                    Redeemed ({claimedCount})
                                  </Button>
                                </ButtonGroup>
                              </Col>
                            </Row>
                          )}
                        </CardHeader>
                        <CardBody className="p-0">
                          {order.status !== 'paid' && order.paymentType !== 'individual' ? (
                            <p className="text-muted text-center py-4">
                              Codes will be available after payment is completed.
                            </p>
                          ) : !order.codes || order.codes.length === 0 ? (
                            <p className="text-muted text-center py-4">
                              No codes found for this order.
                            </p>
                          ) : (
                            <div className="table-responsive">
                              <Table className="align-items-center mb-0" hover>
                                <thead className="thead-light">
                                  <tr>
                                    <th>#</th>
                                    <th>Code</th>
                                    <th>Access</th>
                                    {isIndividual && <th>Price</th>}
                                    <th
                                      style={{ cursor: 'pointer', userSelect: 'none' }}
                                      onClick={() => handleSort(order.id, 'status')}
                                    >
                                      {isIndividual ? 'Payment' : 'Status'} <SortIcon orderId={order.id} columnKey="status" />
                                    </th>
                                    <th
                                      style={{ cursor: 'pointer', userSelect: 'none' }}
                                      onClick={() => handleSort(order.id, 'redeemedBy')}
                                    >
                                      Redeemed By <SortIcon orderId={order.id} columnKey="redeemedBy" />
                                    </th>
                                    <th
                                      style={{ cursor: 'pointer', userSelect: 'none' }}
                                      onClick={() => handleSort(order.id, 'claimedAt')}
                                    >
                                      {isIndividual ? 'Paid At' : 'Claimed At'} <SortIcon orderId={order.id} columnKey="claimedAt" />
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {getProcessedCodes(order.codes, order.id).map((code, idx) => (
                                    <tr key={code.id}>
                                      <td>{idx + 1}</td>
                                      <td>
                                        <code
                                          style={{ fontSize: '0.95rem', cursor: 'pointer' }}
                                          title="Click to copy"
                                          onClick={() => copyCode(code.code)}
                                        >
                                          {code.code} <i className="fas fa-copy text-muted" style={{ fontSize: '0.7rem' }} />
                                        </code>
                                      </td>
                                      <td>
                                        <small>{getPlanById(code.planId)?.months || '?'} {getPlanById(code.planId)?.months === 1 ? 'Month' : 'Months'}</small>
                                      </td>
                                      {isIndividual && (
                                        <td><small>${code.priceAmount}</small></td>
                                      )}
                                      <td>
                                        {isIndividual ? (
                                          code.subscriptionActivated ? (
                                            <Badge color="success">Activated</Badge>
                                          ) : code.paymentStatus === 'paid' ? (
                                            <Badge color="primary">Paid</Badge>
                                          ) : (
                                            <Badge color="warning">Unpaid</Badge>
                                          )
                                        ) : (
                                          <Badge
                                            color={
                                              code.status === 'claimed'
                                                ? 'success'
                                                : 'warning'
                                            }
                                          >
                                            {code.status === 'claimed'
                                              ? 'Redeemed'
                                              : 'Available'}
                                          </Badge>
                                        )}
                                      </td>
                                      <td>
                                        {code.claimedByHandle || code.paidByHandle || (
                                          <span className="text-muted">—</span>
                                        )}
                                      </td>
                                      <td>
                                        {isIndividual
                                          ? (code.paidAt ? formatDate(code.paidAt) : <span className="text-muted">—</span>)
                                          : (code.claimedAt ? formatDate(code.claimedAt) : <span className="text-muted">—</span>)
                                        }
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                          )}
                        </CardBody>
                      </Card>
                    );
                  })
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

const mapStateToProps = (state) => ({
  fetchMyGroupOrders: state.fetchMyGroupOrders,
  activeSpecialty: state.specialty.activeSpecialty,
});

export default connect(mapStateToProps)(GroupCodes);
